import { useEffect } from 'react';
import { useLocationStore, isSufficientLocation } from '../store/useLocationStore';
import { useLocale } from '../hooks/useLocale';
import type { GetLocaleParams } from './localeService';

/**
 * Headless component that resolves the user's location into a locale ID.
 * Mount once near the app root (after QueryClientProvider). When the location
 * store has a sufficient address but no localeId, it fires GET /locale and
 * writes the resolved ID back into the store.
 */
export const InitLocale: React.FC = () => {
  const location = useLocationStore((s) => s.location);
  const locale = useLocationStore((s) => s.locale);
  const setLocale = useLocationStore((s) => s.setLocale);

  const hasSufficient = isSufficientLocation(location);
  // Re-resolve when: no locale cached at all, OR the cached locale is
  // missing a field that ships with the current Locale shape. Catches
  // localStorage values written before a field was added to the API
  // response (today: `maxShareConnectionLevel`; future fields can be
  // added to this check the same way). The BE always includes the
  // field after the rollout, so once a fresh fetch lands the gate
  // stops triggering.
  const needsResolve =
    hasSufficient && (!locale || locale.maxShareConnectionLevel === undefined);

  // Pass lat/lon AND postalCode when both are known — the BE uses lat/lon for
  // spatial-first dish-locale lookups and treats the explicit postalCode as the
  // fallback when its own reverse-geocode doesn't include one.
  const params: GetLocaleParams | null = needsResolve
    ? {
        lat: location!.lat,
        lon: location!.lon,
        postalCode: location!.postalCode,
      }
    : null;

  const { data } = useLocale(params);

  useEffect(() => {
    // Replace the cached locale when we have a fresh response AND either
    // there was no cached locale OR the cached one was stale-shape.
    if (data && (!locale || locale.maxShareConnectionLevel === undefined)) {
      setLocale(data);
    }
  }, [data, locale, setLocale]);

  return null;
};
